import {
    Button,
    styled,
    TextField,
} from '@mui/material';
import Tokens from '@uipath/apollo-core';
import React, {
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    getLabelStudioTemplate,
    updateLabelStudioTemplate,
} from '../../../api/client/dataLabelling';
import { useExternalScript } from '../../../api/global/useExternalScripts';
import { useExternalStyle } from '../../../api/global/useExternalStyle';
import { useFeedback } from '../../../api/global/useFeedback';
import Label from '../../../components/Label';
import Section from '../../../components/Section';
import { WithVisibility } from '../../../components/WithVisibility';
import {
    DATALABELLING_ADVANCED_TAB_TEMPLATE_UPDATE,
    DATALABELLING_GENERAL_TAB_TEMPLATE_UPDATE,
} from '../../../constants/TelemetryConstants';
import { AppPermissions } from '../../../enums/Authorization';
import { PermissionsContext } from '../../../providers/PermissionsProvider';
import { ProjectsContext } from '../../../providers/ProjectsProvider';
import { extractErrorMessage } from '../../../utils/CommonUtils';
import logger from '../../../utils/Logging';
import { recursiveMultiSearch } from '../../../utils/searchObject';
import {
    parseObjectToXml,
    parseXmlToObject,
} from '../../../utils/xmlParser';
import GeneralEditorDetails from '../details/GeneralEditorDetails';
import { initializeLabelStudio } from './initializeLabelStudio';

const assetsPath = process.env.NODE_ENV === 'development' ? '' : 'AIF_APP_ASSETS_PATH';
const labelStudioUrl = assetsPath + '/label-studio/main.js';
const labelStudioCSSrl = assetsPath + '/label-studio/main.css';

const ConfigureTab = styled('div')({
    display: 'flex',
    '& #tabs': { width: '20%' },
    '& #tab-content': { width: '100%' },
    '& .MuiTextField-root': { width: '100%' },
});

const SubmitTemplateButton = styled(Button)({ marginRight: '32px' });

const WarningTextContent = styled('div')(({ theme }) => ({
    borderRadius: '15px',
    maxWidth: '50vw',
    background: 'rgba(235, 197, 0, 0.15)',
    padding: '10px 15px 10px 15px',
    color: theme.palette.semantic.colorForeground,
    marginBottom: '25px',
    '& p': {
        fontFamily: Tokens.FontFamily.FontNormal,
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '21px',
        color: '#526069',
    },
    '& label': {
        marginTop: '5px',
        fontFamily: Tokens.FontFamily.FontTitle,
        fontStyle: 'normal',
        fontWeight: '500',
        fontSize: '14px',
        lineHeight: '24px',
    },
}));

const TemplateContainer = styled('div')({
    width: '48%',
    border: 'solid 1px grey',
    borderRadius: '4px',
});

const ConfigureTemplateWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
});

export const ConfigureLabelling: React.FC<any> = ({
    isConfigureFirst, isGeneralTab,
}) => {
    const { t } = useTranslation();
    useExternalStyle(labelStudioCSSrl);
    const scriptState = useExternalScript(labelStudioUrl);
    const isLabelStudioReady = scriptState === 'ready';
    const [ configureMode, setConfigureMode ] = useState(isConfigureFirst);
    const [ isTabEditable, setIsTabEditable ] = useState(isConfigureFirst || false);
    const openConfigureMode = useCallback(() => {
        setConfigureMode(true);
        setIsTabEditable((prev: boolean) => !prev);
    }, [ setConfigureMode ]);

    const initializeTemplate = () => {
        // eslint-disable-next-line promise/catch-or-return
        getLabelStudioTemplate(appId, projectId).then((r: any) => {
            setTemplate(r.data.template);
            updateGeneralTemplate(r.data.template);
            return true;
        });
    };

    const closeConfigureMode = useCallback(() => {
        initializeTemplate();
        setIsTabEditable((prev: boolean) => !prev);
        return setConfigureMode(false);
    }, [ setConfigureMode ]);

    const locationState: any = useLocation()?.state;
    const appId = locationState?.appId;
    const projectId = locationState?.projectId;

    const { currentProject } = useContext(ProjectsContext).state;
    const { state: permissionsState } = useContext(PermissionsContext);
    const permissions = permissionsState.projectData[currentProject?.id ?? '']?.permissionSet || [];
    const [ , setLabelStudio ] = useState();

    React.useEffect(() => {
        if (appId) {
            initializeTemplate();
        }
    }, [ appId ]);

    const [ template, setTemplate ] = useState('');
    const [ templateObj, setTemplateObj ] = useState<Record<string, any>>({});
    const [ searchedTemplateObj, setSearchedTemplateObj ] = useState<Record<string, any>>();
    const [ isEdited, setIsEdited ] = useState<boolean>(false);
    const feedback = useFeedback();

    const updateGeneralTemplate = (updatedTemplateObj: string): void => {
        const parsedXML = parseXmlToObject(updatedTemplateObj);
        setSearchedTemplateObj(recursiveMultiSearch(parsedXML, [ 'Choices', 'Labels', 'Text' ]));
        setTemplateObj(parsedXML);
    };

    const submitConfiguration = useCallback(() => {
        updateLabelStudioTemplate(appId, projectId, template).then(() => {
            setConfigureMode(false);
            setIsTabEditable((prev: boolean) => !prev);
            updateGeneralTemplate(template);
            feedback.enqueueSuccess('Template updated');

            const telemetryPayload = {
                appId,
                accountId: currentProject?.accountId,
                tenantId: currentProject?.tenantId,
                projectId,
            };

            if (isGeneralTab) {
                /* Add telemtery events when the template is updated with general tab */
                logger.customEvents(
                    {
                        identifier: DATALABELLING_GENERAL_TAB_TEMPLATE_UPDATE,
                        message: 'Data Labeling Template updated through General Tab',
                        payload: { ...telemetryPayload },
                    },
                    DATALABELLING_GENERAL_TAB_TEMPLATE_UPDATE,
                );
            } else {
                /* Add telemtery events when the template is updated with advanced tab */
                logger.customEvents(
                    {
                        identifier: DATALABELLING_ADVANCED_TAB_TEMPLATE_UPDATE,
                        message: 'Data Labeling Template updated through Advanced Tab',
                        payload: { ...telemetryPayload },
                    },
                    DATALABELLING_ADVANCED_TAB_TEMPLATE_UPDATE,
                );
            }

            return true;
        })
            .catch((error: any) => {
                feedback.enqueueError(extractErrorMessage(error, 'Error updating template', { 72004: {} }));
                return false;
            });
    }, [ setConfigureMode, template ]);

    useEffect(() => {
        if (!configureMode || !template) {
            return;
        }
        if (!isLabelStudioReady) {
            return;
        }
        setLabelStudio(initializeLabelStudio(template));
    }, [ template, configureMode, isLabelStudioReady ]);

    useEffect(() => {
        const xmlObj = templateObj;
        const parsedXML = parseObjectToXml(xmlObj);
        setIsEdited(false);
        setTemplate(parsedXML);
    }, [ isEdited === true ]);

    useEffect(() => {
        setLabelStudio(initializeLabelStudio(template));
    }, [ isTabEditable, template ]);

    return (<Section>
        <ConfigureTab
            id='labeling-configuration'>
            <div id="tab-content">
                <ConfigureTemplateWrapper>
                    {isGeneralTab ?
                        (<TemplateContainer>
                            <GeneralEditorDetails
                                templateObj={templateObj}
                                setTemplateObj={setTemplateObj}
                                searchedTemplateObj={searchedTemplateObj}
                                isEdited={isEdited}
                                setIsEdited={(): void => setIsEdited(true)}
                                isTabEditable={isTabEditable} />
                        </TemplateContainer>) :
                        (<TemplateContainer>
                            <TextField
                                multiline
                                data-testid="reconfigure-textarea"
                                maxRows={20}
                                value={template}
                                onChange={(event): void => {
                                    setTemplate(event.target.value);
                                }}
                                InputProps={{
                                    'inputProps': {
                                        'aria-label': t('a11y_template_text'),
                                        disabled: !isTabEditable,
                                    },
                                }}
                            />
                        </TemplateContainer>)}
                    <div style={{ width: '48%' }}>
                        <div id="label-studio" />
                    </div>
                </ConfigureTemplateWrapper>
                <WithVisibility visible={!isTabEditable}>
                    <WarningTextContent
                        id="warning-text">
                        <Label value={t('reconfigure-warning-title')} />
                        {t('reconfigure-warning-text')}
                        <WithVisibility visible={permissions.indexOf(AppPermissions.DataLabeling_Edit) > -1}>
                            <Button
                                onClick={openConfigureMode}
                                data-testid="reconfigure"
                                aria-label="reconfigure"
                                variant="outlined"
                                style={{
                                    display: 'block',
                                    marginTop: '1em',
                                }}>
                                {t('reconfigure-session')}
                            </Button>
                        </WithVisibility>
                    </WarningTextContent>
                </WithVisibility>
                <WithVisibility visible={isTabEditable}>
                    <SubmitTemplateButton
                        onClick={submitConfiguration}
                        data-testid="reconfigure-submit"
                        aria-label={t('form_done_button_text')}
                        variant="outlined">
                        {t('form_done_button_text')}
                    </SubmitTemplateButton>
                    <Button
                        onClick={closeConfigureMode}
                        data-testid="reconfigure-cancel"
                        aria-label={t('form_cancel_button_text')}
                        variant="outlined">
                        {t('form_cancel_button_text')}
                    </Button>
                </WithVisibility>
            </div>
        </ConfigureTab>
    </Section >);
};
